import { TypePolicies } from "@apollo/client";

import {
  Delta,
  DriftDetectionIntegration,
  Maybe,
  ModuleMetadata,
  ModuleRepositoryMetadata,
  OpenTofuRuntimeConfig,
  RuntimeConfig,
  ScanRunResourceStats,
  SpaceliftMetrics,
  TerraformProviderDocs,
  TerraformRuntimeConfig,
  TerragruntRuntimeConfig,
  UserGuideMetadata,
  UserGuideProgress,
  Version,
} from "types/generated";

import { infiniteScrollArrayFieldPolicy, infiniteScrollFieldPolicy } from "./helpers";

const TYPE_POLICIES: TypePolicies = {
  Query: {
    fields: {
      searchVCSIntegrations: {
        keyArgs: ["input"],
      },
      availableAIModels: {
        keyArgs: ["provider", "apiKey", "baseURL", "space"],
      },
      metrics: {
        merge(existing: Maybe<SpaceliftMetrics>, incoming: Maybe<SpaceliftMetrics>) {
          return { ...existing, ...incoming };
        },
      },
      metricsRange: {
        merge(existing: Maybe<SpaceliftMetrics>, incoming: Maybe<SpaceliftMetrics>) {
          return { ...existing, ...incoming };
        },
        keyArgs: false,
      },
      activeUserGuideProgress: {
        merge(existing: Maybe<UserGuideProgress>, incoming: Maybe<UserGuideProgress>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
      completedUserGuides: {
        merge: false,
      },
    },
  },
  AuditTrailWebhook: {
    keyFields: ["endpoint"],
  },
  ConfigElement: {
    keyFields: ["id", "type"],
  },
  Commit: {
    keyFields: ["hash"],
  },
  Run: {
    fields: {
      delta: {
        merge(existing: Maybe<Delta>, incoming: Maybe<Delta>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
      runtimeConfig: {
        merge(existing: Maybe<RuntimeConfig>, incoming: Maybe<RuntimeConfig>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
    },
  },
  RuntimeConfig: {
    fields: {
      terraform: {
        merge(existing: Maybe<TerraformRuntimeConfig>, incoming: Maybe<TerraformRuntimeConfig>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
      terragrunt: {
        merge(existing: Maybe<TerragruntRuntimeConfig>, incoming: Maybe<TerragruntRuntimeConfig>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
      opentofu: {
        merge(existing: Maybe<OpenTofuRuntimeConfig>, incoming: Maybe<OpenTofuRuntimeConfig>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
    },
  },
  Stack: {
    fields: {
      runs: infiniteScrollArrayFieldPolicy(),
      tasks: infiniteScrollArrayFieldPolicy(),
    },
  },
  StackIntegrations: {
    merge: true,
    fields: {
      driftDetection: {
        merge(existing: Maybe<DriftDetectionIntegration>, incoming: Maybe<DriftDetectionIntegration>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
    },
  },
  Module: {
    fields: {
      versions: {
        keyArgs: ["includeFailed"],
        merge(existing: Map<string, Version>, incoming: Version[], { readField }) {
          const merged = new Map<string, Version>(existing);

          for (const item of incoming) {
            const id = readField<string>("id", item);

            if (id) {
              merged.set(id, item);
            }
          }

          return merged;
        },
        read(existing: Map<string, Version>) {
          return existing && Array.from(existing.values());
        },
      },
    },
  },
  Version: {
    fields: {
      metadata: {
        merge(existing: ModuleRepositoryMetadata, incoming: ModuleRepositoryMetadata) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  ModuleRepositoryMetadata: {
    fields: {
      root: {
        merge(existing: Maybe<ModuleMetadata>, incoming: Maybe<ModuleMetadata>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
    },
  },
  ScanRun: {
    fields: {
      resourceStats: {
        merge(existing: Maybe<ScanRunResourceStats>, incoming: Maybe<ScanRunResourceStats>) {
          if (!incoming) return incoming;
          return { ...existing, ...incoming };
        },
      },
    },
  },
  Repository: {
    keyFields: ["name", "namespace"],
  },
  BlueprintInput: {
    keyFields: false,
  },
  TemplateDeploymentInput: {
    keyFields: false,
  },
  TemplateDeployment: {
    fields: {
      inputs: {
        merge: false,
      },
    },
  },
  VCSIntegration: {
    keyFields: ["id", "provider"],
  },
  // id is the provider's PR number (unique only per stack/module), so the parent owns it.
  PullRequest: {
    keyFields: false,
  },
  SpaceliftMetrics: {
    keyFields: false,
  },
  Space: {
    keyFields: false,
  },
  UserGuide: {
    fields: {
      metadata: {
        merge(existing: UserGuideMetadata, incoming: UserGuideMetadata) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  WorkerPool: {
    fields: {
      searchUsers: infiniteScrollFieldPolicy(),
      searchWorkers: infiniteScrollFieldPolicy(),
      searchSchedulableRuns: infiniteScrollFieldPolicy(),
    },
  },
  PublicWorkerPool: {
    keyFields: [],
    fields: {
      searchUsers: infiniteScrollFieldPolicy(),
      searchSchedulableRuns: infiniteScrollFieldPolicy(),
    },
  },
  GithubIntegration: {
    keyFields: [],
  },
  OriginIntegration: {
    keyFields: [],
  },
  Seats: {
    keyFields: [],
  },
  StackIntegrationGcp: {
    merge: true,
  },
  ApiKeyOIDCSettings: {
    merge: true,
  },
  StackOutput: {
    keyFields: false,
  },
  TerraformProviderVersion: {
    fields: {
      docs: {
        merge(existing: Maybe<TerraformProviderDocs>, incoming: Maybe<TerraformProviderDocs>) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
};

export default TYPE_POLICIES;
